<template>
  <TemplateConfirmModal
    ref="confirmModal"
    :template="selectedTemplate"
    :category="selectedCategory"
    :installing="installing"
    :deleting="deleting"
    :installed="isSelectedTemplateInstalled"
    @confirm="installSelectedTemplate"
    @open="openInstalledTemplate"
    @delete="deleteInstalledTemplate"
  />
</template>

<script>
import TemplateService from '@baserow/modules/core/services/template'
import { notifyIf } from '@baserow/modules/core/utils/error'
import TemplateConfirmModal from '@baserow/modules/core/components/template/TemplateConfirmModal'

export default {
  name: 'TemplateUseConfirmation',
  components: { TemplateConfirmModal },
  props: {
    workspace: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      selectedTemplate: null,
      selectedCategory: null,
      installing: false,
      deleting: false,
      job: null,
    }
  },
  computed: {
    selectedInstallation() {
      return this.selectedTemplate?.installation || null
    },
    isSelectedTemplateInstalled() {
      return Boolean(
        this.selectedTemplate?.is_installed && this.selectedInstallation
      )
    },
  },
  watch: {
    'job.state'(newState) {
      if (['finished', 'failed'].includes(newState)) {
        this.installing = false
      }
      if (newState === 'finished') {
        this.$emit('installed', this.job)
      }
    },
  },
  methods: {
    show(template, category = null) {
      this.selectedTemplate = template
      this.selectedCategory = category
      this.installing = false
      this.deleting = false
      this.job = null

      this.$nextTick(() => {
        if (this.selectedTemplate && this.$refs.confirmModal) {
          this.$refs.confirmModal.show()
        }
      })
    },
    openInstalledTemplate() {
      const applicationIds = this.selectedInstallation?.application_ids || []
      const openApplicationId = this.selectedInstallation?.open_application_id
      const orderedApplicationIds = openApplicationId
        ? [
            openApplicationId,
            ...applicationIds.filter((id) => id !== openApplicationId),
          ]
        : applicationIds
      const application = orderedApplicationIds
        .map((id) => this.$store.getters['application/get'](id))
        .find(Boolean)

      if (application) {
        const type = this.$registry.get('application', application.type)
        type.select(application, this)
      }

      if (this.$refs.confirmModal) {
        this.$refs.confirmModal.hide()
      }

      this.$emit('opened', this.selectedInstallation)
    },
    async deleteInstalledTemplate() {
      const installationId = this.selectedInstallation?.id
      const applicationIds = this.selectedInstallation?.application_ids || []
      if (this.deleting || !installationId) {
        return
      }

      this.deleting = true

      try {
        await TemplateService(this.$client).deleteInstallation(installationId)
        applicationIds
          .map((id) => this.$store.getters['application/get'](id))
          .filter(Boolean)
          .forEach((application) => {
            this.$store.dispatch('application/forceDelete', application)
          })
        await this.redirectToWorkspace()
        if (this.$refs.confirmModal) {
          this.$refs.confirmModal.hide()
        }
        this.$emit('deleted', installationId)
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('workspace-menu-entries-updated', {
              detail: { workspaceId: this.workspace.id },
            })
          )
        }
      } catch (error) {
        notifyIf(error, 'template')
      } finally {
        this.deleting = false
      }
    },
    async redirectToWorkspace() {
      const route = {
        name: 'workspace',
        params: { workspaceId: this.workspace.id },
      }
      if (
        this.$route.name === route.name &&
        Number(this.$route.params.workspaceId) === Number(this.workspace.id)
      ) {
        return
      }

      try {
        await this.$router.push(route)
      } catch (error) {
        if (error?.name !== 'NavigationDuplicated') {
          throw error
        }
      }
    },
    async installSelectedTemplate() {
      if (
        this.installing ||
        this.isSelectedTemplateInstalled ||
        !this.selectedTemplate
      ) {
        return
      }

      this.installing = true

      try {
        const { data: job } = await TemplateService(this.$client).asyncInstall(
          this.workspace.id,
          this.selectedTemplate.id
        )
        this.job = job
        this.$store.dispatch('job/create', job)
        this.$emit('install-started', job)

        if (this.$refs.confirmModal) {
          this.$refs.confirmModal.hide()
        }
      } catch (error) {
        notifyIf(error, 'template')
        this.installing = false
      }
    },
  },
}
</script>
